// How an ended share's owner resumes it: the one command, to copy, which
// resumes its session on their machine and shares it again under the same
// id (RFC-043 "Resuming a share", byj-1wv.12). Shown only to the owner of
// an ended share whose session the relay still names, of a kind byj-share
// resumes: a byj, Claude Code or Codex session. It refuses a terminal
// (start it again) and an agent (`resumeArgv` in cmd/byj-share).
import type { ShareSummary } from "../api";
import { canonicalSource, type Source } from "../protocol";
import { t } from "../i18n";
import { CopyButton } from "./Copy";
import { Popover } from "./Popover";

const resumable: Source[] = ["byj", "claude", "codex"];

export function canResume(s: Pick<ShareSummary, "role" | "live" | "session" | "source">): boolean {
  return s.role === "owner" && !s.live && !!s.session?.id && resumable.includes(canonicalSource(s.source));
}

/**
 * A row's Resume, on /shares and on a group's page: a button that opens the command beside it, to
 * read before it is copied, with its Copy (components/Popover). Open, the
 * focus is on Copy; Esc gives it back to Resume, as a second press on Resume
 * does (useDismiss's keepFocus), and a press outside or the focus leaving
 * closes it. Where there is no clipboard the command is there to select by
 * hand (one click selects it).
 */
export function ResumeButton({ share, title }: { share: string; title: string }) {
  const cmd = t("session:resumeCommand", { share });
  return (
    <Popover
      label={t("shares:resumeFor", { title })}
      button={
        <>
          <span aria-hidden="true">↻</span> {t("session:resume")}
        </>
      }
      buttonLabel={t("shares:resumeFor", { title })}
      buttonClassName="small resume-button"
      buttonData={{ "data-command": cmd }}
      className="resume"
      popClassName="resume-pop"
      focus=".resume-pop-command button"
      closeOnFocusOut
    >
      {() => (
        <>
          <p>{t("session:resumeHint")}</p>
          <div className="resume-pop-command">
            <code>{cmd}</code>
            <CopyButton text={cmd} />
          </div>
        </>
      )}
    </Popover>
  );
}

export function ResumeHint({ share }: { share: string }) {
  const cmd = t("session:resumeCommand", { share });
  return (
    <span className="resume-hint row wrap" data-testid="resume-hint">
      <span className="muted">{t("session:resumeHint")}</span>
      <code>{cmd}</code>
      <CopyButton text={cmd} />
    </span>
  );
}
